import { useState } from 'react';
import { nip19 } from 'nostr-tools';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, ShieldCheck, UserPlus } from 'lucide-react';

interface ManualGrantCardProps {
  /** Issue a grant for the given hex pubkey. Resolves when published. */
  onIssue: (businessPubkey: string) => Promise<void>;
  isSubmitting: boolean;
}

/** Resolve an npub / nprofile / hex string to a 64-char hex pubkey, or null. */
function toHexPubkey(input: string): string | null {
  const value = input.trim();
  if (!value) return null;
  if (/^[0-9a-f]{64}$/i.test(value)) return value.toLowerCase();
  if (/^(npub|nprofile)1/i.test(value)) {
    try {
      const decoded = nip19.decode(value);
      if (decoded.type === 'npub' && typeof decoded.data === 'string') {
        return decoded.data.toLowerCase();
      }
      if (decoded.type === 'nprofile' && decoded.data?.pubkey) {
        return decoded.data.pubkey.toLowerCase();
      }
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Backup entry point: manually grant a verified badge to any business by
 * pasting its npub or hex pubkey — for businesses that haven't submitted a
 * payment claim through the app.
 */
export function ManualGrantCard({ onIssue, isSubmitting }: ManualGrantCardProps) {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');

  const hex = toHexPubkey(input);
  const valid = !!hex;

  const handleGrant = async () => {
    setError('');
    if (!hex) {
      setError('Enter a valid npub or 64-character hex pubkey.');
      return;
    }
    try {
      await onIssue(hex);
      setInput('');
    } catch {
      // toast is handled by the caller
    }
  };

  return (
    <Card className="border-dashed">
      <CardContent className="py-4 px-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center size-8 rounded-lg bg-bch/10 shrink-0">
            <UserPlus className="size-4 text-bch" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold">Manually grant a badge</h3>
            <p className="text-xs text-muted-foreground">
              Backup: verify any business by npub, even without a payment claim.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="npub1… or hex pubkey"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="font-mono text-xs"
            disabled={isSubmitting}
            aria-invalid={!!input && !valid}
          />
          <Button
            className="bg-bch hover:bg-bch/90 text-bch-foreground shrink-0"
            onClick={handleGrant}
            disabled={isSubmitting || !valid}
          >
            {isSubmitting ? (
              <Loader2 className="size-4 mr-1.5 animate-spin" />
            ) : (
              <ShieldCheck className="size-4 mr-1.5" />
            )}
            Grant badge
          </Button>
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}
        {input && valid && (
          <p className="text-[11px] text-muted-foreground font-mono break-all">
            → {hex}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
